import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SmartMenuOnboarding } from '@components/v4/onboarding/SmartMenuOnboarding';
import { createDraftSession } from '@services/partnerService';
import { usePartnerAuth } from '../PartnerAuthContext';
import { Button, Card, ErrorNote, Muted, PageShell, Title } from '../ui';

// Reusa el smart menu sobre el draft. Por detras usa el JWT-draft como TOKEN DE COMERCIO en
// localStorage (token + id_commerce) — el interceptor de axiosInstance lo usa para los POST del
// smart menu a los endpoints del panel (/v1/categoria, etc.). Se limpia al desmontar.
const KEYS = ['token', 'id_commerce'] as const;

const PartnerDraftMenu = () => {
  const { id } = useParams<{ id: string }>();
  const { token } = usePartnerAuth();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saved = useRef<Record<string, string | null>>({});
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      if (!token || !id) {
        setError('Sesion de partner no valida.');
        return;
      }
      try {
        const session = await createDraftSession(token, id);
        // Guarda los valores previos y coloca el token de comercio del draft.
        KEYS.forEach((k) => {
          saved.current[k] = localStorage.getItem(k);
        });
        localStorage.setItem('token', session.jwt);
        localStorage.setItem('id_commerce', session.id_comercio);
        if (mounted.current) setReady(true);
      } catch {
        if (mounted.current) setError('No se pudo abrir la sesion del borrador.');
      }
    })();
    return () => {
      mounted.current = false;
      // Restaura/limpia para NO contaminar el panel con el token del draft.
      KEYS.forEach((k) => {
        const prev = saved.current[k];
        if (prev === null || prev === undefined) localStorage.removeItem(k);
        else localStorage.setItem(k, prev);
      });
    };
  }, [token, id]);

  const onComplete = () => navigate('/partner');

  if (error) {
    return (
      <PageShell narrow>
        <Card>
          <Title>Menu del borrador</Title>
          <ErrorNote>{error}</ErrorNote>
          <Button variant="secondary" onClick={() => navigate('/partner')} style={{ marginTop: 14 }}>
            Volver al portal
          </Button>
        </Card>
      </PageShell>
    );
  }

  if (!ready) {
    return (
      <PageShell narrow>
        <Card>
          <Title>Preparando el menu...</Title>
          <Muted>Abriendo la sesion del borrador.</Muted>
        </Card>
      </PageShell>
    );
  }

  return <SmartMenuOnboarding skippable onComplete={onComplete} trackingSource="cargar_menu" />;
};

export default PartnerDraftMenu;
